import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  changePassword: (data) => api.post('/auth/change-password', data),
  setDeployKey: (data) => api.post('/auth/deploy-key', data),
  getMe: () => api.get('/auth/me'),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

export const accountsAPI = {
  getAll: () => api.get('/accounts'),
  create: (data) => api.post('/accounts', data),
  update: (id, data) => api.put(`/accounts/${id}`, data),
  delete: (id) => api.delete(`/accounts/${id}`),
};

export const customersAPI = {
  getAll: () => api.get('/customers'),
  getOptions: () => api.get('/customers/options'),
  getAccounts: (id) => api.get(`/customers/${id}/accounts`),
  getAvailable: () => api.get('/customers/available-accounts'),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  assign: (id, accountId) => api.post(`/customers/${id}/assign`, { account_id: accountId }),
  rotate: (id, oldAccountId, newAccountId) =>
    api.post(`/customers/${id}/rotate`, { old_account_id: oldAccountId, new_account_id: newAccountId }),
  retire: (id, accountId) => api.post(`/customers/${id}/retire`, { account_id: accountId }),
};

export const brokersAPI = {
  getAll: () => api.get('/brokers'),
  create: (data) => api.post('/brokers', data),
  updateCommission: (id, data) => api.put(`/brokers/${id}/commission`, data),
  getCommissionCalculation: (id) => api.get(`/brokers/${id}/commission-calculation`),
  delete: (id) => api.delete(`/brokers/${id}`),
};

export const commissionsAPI = {
  getHistory: (brokerId) => api.get(`/commissions/${brokerId}`),
  getPayments: (brokerId) => api.get(`/commissions/${brokerId}/payments`),
  recordPayment: (data) => api.post('/commissions/payment', data),
  deletePayment: (id) => api.delete(`/commissions/payment/${id}`),
};

export const statusAPI = {
  getAccounts: () => api.get('/status/accounts'),
  updateStatus: (data) => api.post('/status/update', data),
  updateProfit: (data) => api.post('/status/profit', data),
  setMaster: (data) => api.post('/status/set-master', data),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRotationAlerts: () => api.get('/dashboard/rotation-alerts'),
};

export const reportsAPI = {
  getTransactions: (params) => api.get('/reports/transactions', { params }),
  addTransaction: (data) => api.post('/reports/transactions', data),
  getProfitReport: (params) => api.get('/reports/profit', { params }),
  getProfitDeals: (params) => api.get('/reports/profit/deals', { params }),
  getProfitDaily: (params) => api.get('/reports/profit/daily', { params }),
  getSetupHistory: (params) => api.get('/reports/setup-history', { params }),
  getErrorLogs: (params) => api.get('/reports/error-logs', { params }),
};

export const metricsAPI = {
  getAll: (params) => api.get('/metrics', { params }),
  getOne: (accountId) => api.get(`/metrics/${accountId}`),
  getSnapshots: (accountId, params) => api.get(`/metrics/${accountId}/snapshots`, { params }),
  getDailyStats: (params) => api.get('/metrics/daily-stats', { params }),
  getViolations: (params) => api.get('/metrics/violations', { params }),
};

export const psychologyAPI = {
  getAll: (params) => api.get('/psychology', { params }),
  getOne: (accountId) => api.get(`/psychology/${accountId}`),
  getHistory: (accountId, params) => api.get(`/psychology/${accountId}/history`, { params }),
};

export const telegramAPI = {
  getAll: () => api.get('/telegram'),
  create: (data) => api.post('/telegram', data),
  update: (id, data) => api.put(`/telegram/${id}`, data),
  delete: (id) => api.delete(`/telegram/${id}`),
  testStored: (id) => api.post(`/telegram/${id}/test`),
  testRaw: (data) => api.post('/telegram/test', data),
};

export const mt5API = {
  getSyncAlerts: (since) => api.get('/mt5/sync-alerts', { params: since ? { since } : {} }),
  getAlertHistory: (page = 1, limit = 50, filters = {}) =>
    api.get('/mt5/sync-alerts', {
      params: { history: '1', page, limit, ...filters },
    }),
  fixError: (accountId) => api.post('/mt5/fix-error', { account_id: accountId }),
  algoTrading: (accountId, enable) => api.post('/mt5/algo-trading', { account_id: accountId, enable }),
  autoFix: (accountId, enable) => api.post('/mt5/auto-fix', { account_id: accountId, enable }),
  closePositions: (accountId) => api.post('/mt5/close-positions', { account_id: accountId }),
  closeOrders: (accountId) => api.post('/mt5/close-orders', { account_id: accountId }),
  closeAll: (accountId) => api.post('/mt5/close-all', { account_id: accountId }),
  getEAConfig: (accountId) => api.get(`/mt5/ea-config/${accountId}`),
  deleteAlert: (id) => api.delete(`/mt5/alerts/${id}`),
  deployEA: (data) => api.post('/mt5/deploy', data),
};

export default api;
